"use client";
import { Nft } from "@/api/nfts/types";

import { getDisplayPrice } from "@/shared/currencies";
import { formatDateWithMonth } from "@/shared/dates";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import activitiesApi from "@/api/activities/client";
import { getNetworkPaymentMethods } from "@/shared/config/blockchain/helpers";
import { useWeb3State } from "@/web3/context";

export interface Props {
  nft: Nft;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active) {
    return (
      <div className="bg-white p-2 rounded-lg shadow-lg">
        <p className="text-black p-1">{payload[0].payload.Date}</p>
        <p className="text-[#00A79D] p-1">{`${payload[0].payload.Price} ${payload[0].payload.Label}`}</p>
      </div>
    );
  }

  return null;
};

export default function PriceHistory({ nft }: Props) {
  const { network } = useWeb3State();
  const paymentMethods = getNetworkPaymentMethods(network);

  const { data: purchaseHistory } = useQuery({
    queryKey: ["purchase-history", nft.id],
    queryFn: () => activitiesApi.getPurchaseHistory(nft.id),
  });

  const priceGraphData = useMemo(() => {
    if (!purchaseHistory) return;

    return purchaseHistory.map((activity) => ({
      Date: formatDateWithMonth(activity.createdAt),
      Price: getDisplayPrice(activity.price),
      Label:
        paymentMethods![activity.paymentErc20TokenAddress!.toLowerCase()]
          ?.label,
    }));
  }, [purchaseHistory]);

  const maxPrice = priceGraphData?.reduce((max, item) => {
    const itemNumber = parseFloat(item.Price!);
    return itemNumber > max ? itemNumber : max;
  }, 0);

  const domainY = Math.ceil(Number(maxPrice) * 10) / 10;

  if (!priceGraphData?.length) return null;

  return (
    <>
      <div className="text-center pb-4 mt-10">Price History</div>
      <div className={"h-[300px] md:h-[400px] lg:h-[500px]"}>
        <ResponsiveContainer
          width="100%"
          height="100%"
          className="lg:pl-[30px]"
        >
          <AreaChart
            data={priceGraphData}
            margin={{
              top: 10,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00A79D" stopOpacity="0.5" />
                <stop offset="80%" stopColor="#00A79D" stopOpacity="0" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="Date"
              tick={{ fill: "#9A9A9A" }}
              tickLine={{ stroke: "#EAEAEA" }}
              axisLine={{ stroke: "#EAEAEA" }}
              tickMargin={10}
            />
            <YAxis
              tick={{ fill: "#9A9A9A" }}
              tickLine={{ stroke: "#EAEAEA" }}
              axisLine={{ stroke: "#EAEAEA" }}
              tickMargin={10}
              domain={[0, domainY]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              strokeWidth="5"
              dataKey="Price"
              stroke="#00A79D"
              fill="url(#chartGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}
